// Thin fetch wrappers around the admin wallet endpoints on artist-connect-api.
//
// Each function is curried with a `firebase` instance so callers can pass
// `firebase` from the `withFirebase` HOC without leaking auth concerns into
// component code.

import { request } from './adminApi';

/**
 * Fetch a single user's wallet, balance, and recent ledger.
 *
 * @param {object} firebase
 * @returns {(userId: string) => Promise<{ balance: number, updated_at: number, transactions: Array, next_cursor: ?string }>}
 */
export const getUserWallet = (firebase) => async (userId) => {
  return request(firebase, `/admin/wallets/${encodeURIComponent(userId)}`, {
    method: 'GET',
  });
};

/**
 * Gift the same number of credits to many users in one batch.
 *
 * The API may return partial failures. `failed` entries with `credited: true`
 * mean the wallet was credited but the ledger append failed — callers should
 * surface this distinction rather than offering a naive retry.
 *
 * @param {object} firebase
 * @returns {(input: { userIds: string[], creditsPerUser: number, note: string }) => Promise<{ gift_batch_id: string, succeeded: Array, failed: Array }>}
 */
export const bulkGift = (firebase) => async ({ userIds, creditsPerUser, note }) => {
  return request(firebase, '/admin/wallets/gift', {
    method: 'POST',
    body: JSON.stringify({
      user_ids: userIds,
      credits_per_user: creditsPerUser,
      note,
    }),
  });
};

/**
 * Apply a signed adjustment to a single user's wallet.
 *
 * @param {object} firebase
 * @returns {(input: { userId: string, delta: number, note: string }) => Promise<{ balance: number }>}
 */
export const adjustWallet = (firebase) => async ({ userId, delta, note }) => {
  return request(firebase, '/admin/wallets/adjust', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      delta,
      note,
    }),
  });
};
